/**
 * Detail Page PDF Layout
 *
 * Creates a comprehensive layout for individual detail pages.
 * Works with Day Details, Place Details, Travel Details, etc.
 * Displays complete details as shown in the application.
 */

export class DetailPagePDFLayout {
  constructor() {
    this.layoutType = 'mixed';
    this.name = 'Detail Page Layout';
  }

  /**
   * Generate PDF content structure for detail pages
   * @param {Object} pageContext - Page context with detail data
   * @returns {Object} PDF content structure
   */
  async generate(pageContext) {
    const detailData = pageContext.data;

    console.log('🏨 [DEBUG] DetailPagePDFLayout.generate called:', {
      pageType: pageContext.type,
      hasDetailData: !!detailData,
      detailDataKeys: detailData ? Object.keys(detailData) : [],
      detailDataType: typeof detailData,
      pageContextKeys: Object.keys(pageContext),
      title: pageContext.title
    });

    if (!detailData || (typeof detailData === 'object' && Object.keys(detailData).length === 0)) {
      console.log('🏨 [DEBUG] No detail data found, generating empty content');
      return this.generateEmptyContent(pageContext);
    }

    // Generate layout based on page type
    console.log('🏨 [DEBUG] Switching on page type:', pageContext.type);
    switch (pageContext.type) {
      case 'Day Detail':
        console.log('🏨 [DEBUG] Calling generateDayDetailLayout');
        return await this.generateDayDetailLayout(detailData, pageContext);
      case 'Place Detail':
        console.log('🏨 [DEBUG] Calling generatePlaceDetailLayout');
        return await this.generatePlaceDetailLayout(detailData, pageContext);
      case 'Travel Detail':
        console.log('🏨 [DEBUG] Calling generateTravelDetailLayout');
        return this.generateTravelDetailLayout(detailData, pageContext);
      case 'Accommodation Detail':
        console.log('🏨 [DEBUG] Calling generateAccommodationDetailLayout');
        return await this.generateAccommodationDetailLayout(detailData, pageContext);
      case 'Document Detail':
        console.log('🏨 [DEBUG] Calling generateDocumentDetailLayout');
        return this.generateDocumentDetailLayout(detailData, pageContext);
      case 'Extras List':
        console.log('🏨 [DEBUG] Calling generateExtrasLayout');
        return this.generateExtrasLayout(detailData, pageContext);
      default:
        console.log('🏨 [DEBUG] Page type not recognized, calling generateGenericDetailLayout');
        return this.generateGenericDetailLayout(detailData, pageContext);
    }
  }

  /**
   * Generate day detail layout - REPLICATES actual Day Detail page structure
   * @param {Object} dayData - Day data
   * @param {Object} pageContext - Page context
   * @returns {Object} Day detail content
   */
  async generateDayDetailLayout(dayData, pageContext) {
    const sections = [];

    // Day Content section (replicates ContentBlockRenderer display). Registry-aware
    // (Issue 17): resolveDayItineraryContent merges in stays_on/travel/place links that
    // registry-only trips no longer write as legacy content blocks — without this, a
    // printed day showed no stay/travel lines at all for those trips.
    let dayContent = dayData.content || dayData.contentBlocks;
    if (dayData.id && pageContext.userId && pageContext.tripId) {
      try {
        const { resolveDayItineraryContent } = await import('@trip-planner-monorepo/shared');
        const resolved = await resolveDayItineraryContent(
          pageContext.userId, pageContext.tripId, dayData.id, dayData.content || []
        );
        dayContent = resolved.content;
      } catch (error) {
        console.warn('DetailPagePDFLayout: could not resolve registry day content, using legacy', error);
      }
    }

    const dayContentSection = this.generateDayContentSection(dayContent);
    if (dayContentSection) {
      sections.push(dayContentSection);
    }

    // Related Documents section (replicates RelatedDocumentsSection)
    const relatedDocsSection = this.generateRelatedDocumentsSection(dayData.contentBlocks);
    if (relatedDocsSection) {
      sections.push(relatedDocsSection);
    }

    // Notes section (replicates Notes component display)
    if (dayData.notes) {
      sections.push(this.generateDayNotesSection(dayData.notes, dayData.lastEditedTime));
    }

    return { sections: sections.filter(section => section !== null) };
  }

  /**
   * Generate place detail layout - REPLICATES actual Place Detail page structure
   * @param {Object} placeData - Place data
   * @param {Object} pageContext - Page context
   * @returns {Object} Place detail content
   */
  async generatePlaceDetailLayout(placeData, pageContext) {
    const sections = [];

    // External Resources (formatted as bullet list, not table)
    sections.push(this.generateExternalResourcesSection(placeData));

    // AI-Generated Resources Section (matching External Resources format)
    const aiResourcesSection = this.generateAIResourcesSection(placeData);
    if (aiResourcesSection) {
      sections.push(aiResourcesSection);
    }

    // Previously Deleted AI Resources Section (always included if data exists)
    const deletedAIResourcesSection = this.generateDeletedAIResourcesSection(placeData);
    if (deletedAIResourcesSection) {
      sections.push(deletedAIResourcesSection);
    }

    // Related Days - SEPARATE SECTIONS PER ACCOMMODATION (matching actual page structure)
    const relatedDaysSections = await this.generatePlaceRelatedDaysSections(placeData, pageContext);
    if (relatedDaysSections && relatedDaysSections.length > 0) {
      sections.push(...relatedDaysSections);
    }

    // Related Documents Section (replicates RelatedDocumentsSection component)
    const relatedDocsSection = this.generatePlaceRelatedDocumentsSection(placeData);
    if (relatedDocsSection) {
      sections.push(relatedDocsSection);
    }

    // Notes Section (replicates Notes component)
    if (placeData.userContent) {
      sections.push(this.generatePlaceNotesSection(placeData.userContent));
    }

    return { sections: sections.filter(section => section !== null) };
  }

  /**
   * Generate travel detail layout
   * @param {Object} travelData - Travel data
   * @param {Object} pageContext - Page context
   * @returns {Object} Travel detail content
   */
  /**
   * Generate travel detail layout - BULLET POINT FORMAT (NO TABLES) - REDESIGNED TO MATCH PLACE/ACCOMMODATION
   * @param {Object} travelData - Travel data
   * @param {Object} pageContext - Page context
   * @returns {Object} Travel detail content
   */
  generateTravelDetailLayout(travelData, pageContext) {
    const sections = [];

    console.log('✈️ [PDF TravelDetail] Starting layout generation:', {
      travelId: travelData.id,
      travelType: travelData.type,
      hasSummary: !!travelData.summary,
      hasDetails: !!travelData.details,
      hasContentBlocks: !!travelData.contentBlocks,
      hasNotes: !!travelData.notes
    });

    // Determine travel type for appropriate section generation
    const travelType = travelData.type || 'generic';

    // 1. Flight/Train/Bus Information section (type-specific)
    console.log('✈️ [PDF TravelDetail] Calling generateTravelFlightInfoSection');
    const flightInfoSection = this.generateTravelFlightInfoSection(travelData);
    if (flightInfoSection) {
      sections.push(flightInfoSection);
    }

    // 2. Schedule section (departure/arrival with times)
    console.log('✈️ [PDF TravelDetail] Calling generateTravelScheduleSection');
    const scheduleSection = this.generateTravelScheduleSection(travelData);
    if (scheduleSection) {
      sections.push(scheduleSection);
    }

    // 3. Booking Information section
    console.log('✈️ [PDF TravelDetail] Calling generateTravelBookingInfoSection');
    const bookingInfoSection = this.generateTravelBookingInfoSection(travelData);
    if (bookingInfoSection) {
      sections.push(bookingInfoSection);
    }

    // 4. Passenger Information section (if applicable for flight/train)
    console.log('✈️ [PDF TravelDetail] Calling generateTravelPassengerInfoSection');
    const passengerInfoSection = this.generateTravelPassengerInfoSection(travelData);
    if (passengerInfoSection) {
      sections.push(passengerInfoSection);
    }

    // 5. Related Documents section
    console.log('✈️ [PDF TravelDetail] Calling generateTravelRelatedDocumentsSection');
    const relatedDocsSection = this.generateTravelRelatedDocumentsSection(travelData);
    if (relatedDocsSection) {
      sections.push(relatedDocsSection);
    }

    // 6. Notes section
    console.log('✈️ [PDF TravelDetail] Calling generateTravelNotesSection');
    const notesSection = this.generateTravelNotesSection(travelData.notes, travelData.updatedAt);
    if (notesSection) {
      sections.push(notesSection);
    }

    console.log('✈️ [PDF TravelDetail] Generated sections:', {
      sectionsCount: sections.length,
      sectionTitles: sections.map(s => s.title || s.type)
    });

    const filteredSections = sections.filter(section => section !== null && section !== undefined);

    console.log('✈️ [PDF TravelDetail] Final sections after filtering:', {
      originalSectionsCount: sections.length,
      filteredSectionsCount: filteredSections.length,
      sectionTitles: filteredSections.map(s => s.title || s.type),
      nullSectionsFiltered: sections.length - filteredSections.length
    });

    return { sections: filteredSections };
  }

  /**
   * Generate accommodation detail layout - BULLET POINT FORMAT (NO TABLES) - FIXED SECTION CALLS
   * @param {Object} accommodationData - Accommodation data
   * @param {Object} pageContext - Page context
   * @returns {Object} Accommodation detail content
   */
  async generateAccommodationDetailLayout(accommodationData, pageContext) {
    const sections = [];

    console.log('🏨 [PDF AccommodationDetail] Starting layout generation:', {
      accommodationId: accommodationData.id,
      accommodationName: accommodationData.details?.name || accommodationData.summary,
      hasDetails: !!accommodationData.details,
      hasContentBlocks: !!accommodationData.contentBlocks,
      hasDaysData: !!(accommodationData._daysData && accommodationData._daysData.length > 0),
      hasNotes: !!accommodationData.notes
    });

    // CALL ALL 7 SECTIONS in the correct order - even if they return null, they get filtered out later

    // 1. Address section (with contact info) - BULLET POINT FORMAT
    console.log('🏨 [PDF AccommodationDetail] Calling generateAccommodationAddressSection');
    const addressSection = this.generateAccommodationAddressSection(accommodationData);
    if (addressSection) {
      sections.push(addressSection);
    }

    // 2. Check-in/Check-out section (matches stay-card structure)
    console.log('🏨 [PDF AccommodationDetail] Calling generateAccommodationStayInfoSection');
    const stayInfoSection = this.generateAccommodationStayInfoSection(accommodationData);
    if (stayInfoSection) {
      sections.push(stayInfoSection);
    }

    // 3. Booking Information section (matches DetailSection title="Booking Information")
    console.log('🏨 [PDF AccommodationDetail] Calling generateAccommodationBookingInfoSection');
    const bookingInfoSection = this.generateAccommodationBookingInfoSection(accommodationData);
    if (bookingInfoSection) {
      sections.push(bookingInfoSection);
    }

    // 4. Related Days section (matches entity-connections structure)
    console.log('🏨 [PDF AccommodationDetail] Calling generateAccommodationRelatedDaysSection');
    const relatedDaysSection = await this.generateAccommodationRelatedDaysSection(accommodationData, pageContext);
    if (relatedDaysSection) {
      sections.push(relatedDaysSection);
    }

    // 5. Related Documents section (matches entity-connections structure)
    console.log('🏨 [PDF AccommodationDetail] Calling generateAccommodationRelatedDocumentsSection');
    const relatedDocsSection = this.generateAccommodationRelatedDocumentsSection(accommodationData);
    if (relatedDocsSection) {
      sections.push(relatedDocsSection);
    }

    // 6. Cost & Status section - BULLET POINT FORMAT
    console.log('🏨 [PDF AccommodationDetail] Calling generateAccommodationCostStatusSection');
    const costStatusSection = this.generateAccommodationCostStatusSection(accommodationData);
    if (costStatusSection) {
      sections.push(costStatusSection);
    }

    // 7. Notes section (matches Notes component)
    console.log('🏨 [PDF AccommodationDetail] Calling generateAccommodationNotesSection');
    const notesSection = this.generateAccommodationNotesSection(accommodationData.notes, accommodationData.updatedAt);
    if (notesSection) {
      sections.push(notesSection);
    }

    console.log('🏨 [PDF AccommodationDetail] Generated sections:', {
      sectionsCount: sections.length,
      sectionTitles: sections.map(s => s.title || s.type),
      allSectionsCalled: true
    });

    const filteredSections = sections.filter(section => section !== null && section !== undefined);

    console.log('🏨 [PDF AccommodationDetail] Final sections after filtering:', {
      originalSectionsCount: sections.length,
      filteredSectionsCount: filteredSections.length,
      sectionTitles: filteredSections.map(s => s.title || s.type),
      nullSectionsFiltered: sections.length - filteredSections.length
    });

    return { sections: filteredSections };
  }

  /**
   * Generate document detail layout
   * @param {Object} documentData - Document data
   * @param {Object} pageContext - Page context
   * @returns {Object} Document detail content
   */
  generateDocumentDetailLayout(documentData, pageContext) {
    const sections = [];

    // Basic document information
    sections.push(this.generateBasicInfoSection(documentData, 'Document Information'));

    // File details
    sections.push(this.generateFileDetailsSection(documentData));

    // Associations
    if (documentData.associatedDays || documentData.associatedPlaces || documentData.tags) {
      sections.push(this.generateAssociationsSection(documentData));
    }

    // Content preview or description
    if (documentData.content || documentData.description) {
      sections.push(this.generateContentPreviewSection(documentData));
    }

    return { sections: sections.filter(section => section !== null) };
  }

  /**
   * Generate extras layout
   * @param {*} extrasData - Extras data
   * @param {Object} pageContext - Page context
   * @returns {Object} Extras content
   */
  generateExtrasLayout(extrasData, pageContext) {
    const sections = [];

    if (Array.isArray(extrasData)) {
      // Multiple extras items
      sections.push({
        type: 'table',
        title: 'Additional Trip Information',
        columns: ['Item', 'Description', 'Notes'],
        rows: extrasData.map(extra => [
          extra.name || extra.title || 'Extra Item',
          extra.description || '-',
          extra.notes || '-'
        ])
      });
    } else if (extrasData && typeof extrasData === 'object') {
      // Single extras object or general information
      sections.push(this.generateBasicInfoSection(extrasData, 'Additional Information'));
    } else {
      // Text content
      sections.push({
        type: 'text',
        title: 'Additional Information',
        content: extrasData?.toString() || 'No additional information available.'
      });
    }

    return { sections: sections.filter(section => section !== null) };
  }

  /**
   * Generate generic detail layout for unknown types
   * @param {*} data - Generic data
   * @param {Object} pageContext - Page context
   * @returns {Object} Generic detail content
   */
  generateGenericDetailLayout(data, pageContext) {
    const sections = [];

    if (typeof data === 'object' && data !== null) {
      sections.push(this.generateBasicInfoSection(data, 'Details'));
    } else {
      sections.push({
        type: 'text',
        title: 'Information',
        content: data?.toString() || 'No information available.'
      });
    }

    return { sections };
  }

  /**
   * Generate basic information section
   * @param {Object} data - Data object
   * @param {string} title - Section title
   * @returns {Object} Basic info section
   */
  generateBasicInfoSection(data, title = 'Information') {
    const rows = [];

    // Common fields
    if (data.name) rows.push(['Name', data.name]);
    if (data.title && data.title !== data.name) rows.push(['Title', data.title]);
    if (data.type) rows.push(['Type', data.type]);
    if (data.category) rows.push(['Category', data.category]);
    if (data.location) rows.push(['Location', data.location]);
    if (data.address) rows.push(['Address', data.address]);
    if (data.date) rows.push(['Date', this.formatDate(data.date)]);
    if (data.status) rows.push(['Status', data.status]);

    // If no specific fields found, show all non-object fields
    if (rows.length === 0) {
      Object.entries(data).forEach(([key, value]) => {
        if (value && typeof value !== 'object' && !key.startsWith('_')) {
          rows.push([this.formatFieldName(key), this.formatValue(value)]);
        }
      });
    }

    return {
      type: 'table',
      title: title,
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate Day Content section - REPLICATES ContentBlockRenderer display exactly
   * @param {Array|string} content - Content blocks or legacy string content
   * @returns {Object} Day content section
   */
  generateDayContentSection(content) {
    // Handle empty content
    if (!content) {
      return {
        type: 'text',
        title: 'Day Content:',
        content: 'No content for this day.'
      };
    }

    // Determine content type (same logic as ContentBlockRenderer)
    const isStructuredContent = Array.isArray(content) && content.length > 0 &&
      content[0] && typeof content[0] === 'object' && content[0].type;

    if (isStructuredContent) {
      // Process structured content blocks into simple text lines
      const contentLines = [];

      content.forEach(block => {
        if (!block || !block.type) return;

        let displayText = '';

        switch (block.type) {
          case 'text':
            displayText = block.text || '';
            break;

          case 'accommodation_reference':
            // PDF-friendly logic without emojis that cause encoding issues
            if (block.displayText) {
              // displayText already contains the full text, don't add "Stay at" again
              displayText = block.displayText;
            } else {
              const accommodationName = block.accommodationName || 'Unknown Accommodation';
              const location = block.placeName || '';
              displayText = location ?
                `Stay at ${accommodationName}: ${location}` :
                `Stay at ${accommodationName}`;
            }
            break;

          case 'travel_reference':
            displayText = `Travel: ${block.displayText || 'Travel arrangement'}`;
            break;

          case 'activity_reference':
            displayText = `Activity: ${block.displayText || 'Activity'}`;
            break;

          default:
            displayText = block.displayText || block.text || 'Unknown content';
        }

        if (displayText.trim()) {
          // Split text by newlines to handle multi-line content within a single block
          const lines = displayText.split(/\n/).map(line => line.trim()).filter(line => line);
          contentLines.push(...lines);
        }
      });

      return {
        type: 'list',
        title: 'Day Content:',
        items: contentLines.length > 0 ? contentLines : ['No content for this day.']
      };

    } else if (typeof content === 'string') {
      // Handle legacy content with accommodation link detection
      return {
        type: 'text',
        title: 'Day Content:',
        content: content
      };
    } else {
      return {
        type: 'text',
        title: 'Day Content:',
        content: 'No content for this day.'
      };
    }
  }

  /**
   * Generate Related Documents section - REPLICATES RelatedDocumentsSection display
   * @param {Array} contentBlocks - Content blocks that may contain document references
   * @returns {Object} Related documents section or null
   */
  generateRelatedDocumentsSection(contentBlocks) {
    if (!contentBlocks || !Array.isArray(contentBlocks)) {
      return null;
    }

    // Extract document references from content blocks
    const documentRefs = contentBlocks.filter(block =>
      block && block.type === 'document_reference'
    );

    if (documentRefs.length === 0) {
      return null;
    }

    const documentList = documentRefs.map(doc =>
      doc.documentTitle || doc.displayText || 'Document'
    );

    return {
      type: 'list',
      title: 'Related Documents:',
      items: documentList
    };
  }

  /**
   * Generate Day Notes section - REPLICATES Notes component display
   * @param {string} notes - Notes content
   * @param {string} lastEditedTime - Last edited timestamp
   * @returns {Object} Notes section
   */
  generateDayNotesSection(notes, lastEditedTime) {
    let content = notes || 'No notes for this day.';

    // Strip HTML tags and convert to readable plain text
    if (content && content !== 'No notes for this day.') {
      content = this.stripHtmlToPlainText(content);
    }

    // Split content into lines for proper PDF formatting
    let lines = [];
    if (content === 'No notes for this day.') {
      lines = [content];
    } else {
      // Split by double newlines (paragraphs) and single newlines
      const paragraphs = content.split(/\n\n+/);
      paragraphs.forEach(paragraph => {
        const trimmed = paragraph.trim();
        if (trimmed) {
          // Split long paragraphs by single newlines too
          const subLines = trimmed.split(/\n/);
          subLines.forEach(line => {
            const trimmedLine = line.trim();
            if (trimmedLine) {
              lines.push(trimmedLine);
            }
          });
          // Add spacing between paragraphs (except for the last one)
          if (paragraphs.indexOf(paragraph) < paragraphs.length - 1) {
            lines.push(''); // Empty line for spacing
          }
        }
      });
    }

    // Add last edited time if available
    if (lastEditedTime) {
      const editedDate = new Date(lastEditedTime).toLocaleDateString();
      if (lines.length > 1 || lines[0] !== 'No notes for this day.') {
        lines.push(''); // Empty line for spacing
      }
      lines.push(`Last edited: ${editedDate}`);
    }

    return {
      type: 'list',
      title: 'Notes:',
      items: lines
    };
  }

  /**
   * Generate activities section for days - REPLICATES ContentBlockRenderer logic exactly
   * @param {Array|string} content - Content blocks/activities or legacy string content
   * @returns {Object} Activities section
   */
  generateActivitiesSection(content) {
    // Handle empty content
    if (!content) {
      return {
        type: 'text',
        title: 'Planned Activities',
        content: 'No content for this day.'
      };
    }

    // Determine content type (same logic as ContentBlockRenderer)
    const isStructuredContent = Array.isArray(content) && content.length > 0 &&
      content[0] && typeof content[0] === 'object' && content[0].type;

    if (isStructuredContent) {
      // Process structured content blocks (same as ContentBlockRenderer)
      const activities = content.map(block => {
        if (!block || !block.type) {
          return ['-', 'Invalid content block', '-'];
        }

        let displayText = '';

        switch (block.type) {
          case 'text':
            displayText = block.text || '';
            break;

          case 'accommodation_reference':
            // EXACT logic from AccommodationBlock component
            if (block.displayText) {
              displayText = block.displayText;
            } else {
              // Parse displayText like AccommodationBlock does
              const accommodationName = block.accommodationName || 'Unknown Accommodation';
              const location = block.placeName || '';
              displayText = location ?
                `Stay at ${accommodationName}: ${location}` :
                `Stay at ${accommodationName}`;
            }
            break;

          case 'travel_reference':
            displayText = block.displayText || 'Travel arrangement';
            break;

          case 'activity_reference':
            displayText = block.displayText || 'Activity';
            break;

          default:
            displayText = block.displayText || block.text || 'Unknown content';
        }

        return [
          block.time || block.startTime || '-',
          displayText,
          block.description || block.notes || '-'
        ];
      });

      return {
        type: 'table',
        title: 'Planned Activities',
        columns: ['Time', 'Activity', 'Description'],
        rows: activities
      };

    } else {
      // Handle legacy content (same as LegacyContentRenderer)
      if (typeof content === 'string') {
        // Process legacy string content with accommodation link detection
        const accommodationRegex = /Stay at ([^:\n]+)(?:: ([^(\n]+))?(?:\s*\(([^)]+)\))?/g;
        let cleanContent = content;
        let activities = [];

        // Extract accommodation references
        let match;
        while ((match = accommodationRegex.exec(content)) !== null) {
          const [fullMatch, name, location, context] = match;
          let displayText = `Stay at ${name}`;
          if (location) displayText += `: ${location}`;
          if (context) displayText += ` (${context})`;

          activities.push(['-', displayText, '-']);
        }

        // If no structured activities found, show as simple text
        if (activities.length === 0) {
          return {
            type: 'text',
            title: 'Planned Activities',
            content: content
          };
        } else {
          return {
            type: 'table',
            title: 'Planned Activities',
            columns: ['Time', 'Activity', 'Description'],
            rows: activities
          };
        }
      } else {
        return {
          type: 'text',
          title: 'Planned Activities',
          content: 'No activities have been planned for this day yet.'
        };
      }
    }
  }

  /**
   * Generate location section for places
   * @param {Object} placeData - Place data
   * @returns {Object} Location section
   */
  generateLocationSection(placeData) {
    const rows = [];

    if (placeData.address) rows.push(['Address', placeData.address]);
    if (placeData.city) rows.push(['City', placeData.city]);
    if (placeData.country) rows.push(['Country', placeData.country]);
    if (placeData.coordinates) rows.push(['Coordinates', placeData.coordinates]);
    if (placeData.phone) rows.push(['Phone', placeData.phone]);
    if (placeData.email) rows.push(['Email', placeData.email]);
    if (placeData.website) rows.push(['Website', placeData.website]);

    return {
      type: 'table',
      title: 'Location & Contact',
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate travel information section
   * @param {Object} travelData - Travel data
   * @returns {Object} Travel info section
   */
  /**
   * Generate Flight/Train/Bus Information section - BULLET POINT FORMAT
   * @param {Object} travelData - Travel data
   * @returns {Object|null} Flight information section
   */
  generateTravelFlightInfoSection(travelData) {
    console.log('✈️ [DEBUG] generateTravelFlightInfoSection called:', {
      travelType: travelData.type,
      hasDetails: !!travelData.details,
      detailsKeys: travelData.details ? Object.keys(travelData.details) : []
    });

    const details = travelData.details || {};
    const flightItems = [];

    // Type-specific information
    if (travelData.type === 'flight') {
      // Airline and Flight Number
      if (details.airline) {
        const flightNumber = details.flightNumber || '';
        flightItems.push(`Airline: ${details.airline}${flightNumber ? ' ' + flightNumber : ''}`);
      }

      // Route (Origin → Destination)
      const departureCity = details.departureCity || details.departureAirport || '';
      const arrivalCity = details.arrivalCity || details.arrivalAirport || '';
      if (departureCity && arrivalCity) {
        flightItems.push(`Route: ${departureCity} → ${arrivalCity}`);
      } else if (departureCity || arrivalCity) {
        flightItems.push(`Route: ${departureCity || 'Unknown'} → ${arrivalCity || 'Unknown'}`);
      }

      // Aircraft type
      if (details.aircraft) {
        flightItems.push(`Aircraft: ${details.aircraft}`);
      }

      // Class of service
      if (details.classOfService) {
        const classDisplay = details.classOfService.charAt(0).toUpperCase() +
                           details.classOfService.slice(1).replace('_', ' ');
        flightItems.push(`Class: ${classDisplay}`);
      }

      // Baggage allowance
      if (details.baggageAllowance) {
        flightItems.push(`Baggage allowance: ${details.baggageAllowance}`);
      }
    } else if (travelData.type === 'train') {
      // Train operator and number
      if (details.operator) {
        const trainNumber = details.trainNumber || '';
        flightItems.push(`Operator: ${details.operator}${trainNumber ? ' ' + trainNumber : ''}`);
      }

      // Route (Origin → Destination)
      const departureStation = details.departureStation || '';
      const arrivalStation = details.arrivalStation || '';
      if (departureStation && arrivalStation) {
        flightItems.push(`Route: ${departureStation} → ${arrivalStation}`);
      }

      // Class
      if (details.classOfService) {
        flightItems.push(`Class: ${details.classOfService}`);
      }
    } else if (travelData.type === 'bus') {
      // Bus operator and service number
      if (details.operator) {
        const serviceNumber = details.serviceNumber || '';
        flightItems.push(`Operator: ${details.operator}${serviceNumber ? ' ' + serviceNumber : ''}`);
      }

      // Route (Origin → Destination)
      const departureStop = details.departureStop || '';
      const arrivalStop = details.arrivalStop || '';
      if (departureStop && arrivalStop) {
        flightItems.push(`Route: ${departureStop} → ${arrivalStop}`);
      }
    } else {
      // Generic travel type - use summary or basic info
      if (travelData.summary) {
        flightItems.push(`Travel: ${travelData.summary}`);
      }
      if (travelData.type) {
        flightItems.push(`Type: ${this.formatTravelType(travelData.type)}`);
      }
    }

    if (flightItems.length === 0) {
      console.log('✈️ [DEBUG] generateTravelFlightInfoSection returning null - no flight items');
      return null;
    }

    console.log('✈️ [DEBUG] generateTravelFlightInfoSection returning section with items:', flightItems);
    return {
      type: 'list',
      title: travelData.type === 'flight' ? 'Flight Information' :
             travelData.type === 'train' ? 'Train Information' :
             travelData.type === 'bus' ? 'Bus Information' : 'Travel Information',
      items: flightItems
    };
  }

  /**
   * Generate Schedule section - BULLET POINT FORMAT
   * @param {Object} travelData - Travel data
   * @returns {Object|null} Schedule section
   */
  generateTravelScheduleSection(travelData) {
    console.log('✈️ [DEBUG] generateTravelScheduleSection called:', {
      hasDetails: !!travelData.details,
      hasDepartureTime: !!(travelData.details?.departureTime),
      hasArrivalTime: !!(travelData.details?.arrivalTime)
    });

    const details = travelData.details || {};
    const scheduleItems = [];

    // Departure information
    if (details.departureTime) {
      try {
        const departureDate = new Date(details.departureTime);
        const dateStr = departureDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        const timeStr = departureDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        // Include airport/station/stop code if available
        let locationStr = '';
        if (travelData.type === 'flight' && details.departureAirport) {
          locationStr = ` (${details.departureAirport})`;
        } else if (travelData.type === 'train' && details.departureStation) {
          locationStr = ` (${details.departureStation})`;
        } else if (travelData.type === 'bus' && details.departureStop) {
          locationStr = ` (${details.departureStop})`;
        }

        scheduleItems.push(`Departure: ${dateStr} ${timeStr}${locationStr}`);

        // Add timezone if available
        if (details.departureTimezone) {
          scheduleItems.push(`Departure timezone: ${details.departureTimezone}`);
        }
      } catch (e) {
        console.warn('Error formatting departure time:', e);
        scheduleItems.push(`Departure: ${details.departureTime}`);
      }
    }

    // Arrival information
    if (details.arrivalTime) {
      try {
        const arrivalDate = new Date(details.arrivalTime);
        const dateStr = arrivalDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        const timeStr = arrivalDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        // Include airport/station/stop code if available
        let locationStr = '';
        if (travelData.type === 'flight' && details.arrivalAirport) {
          locationStr = ` (${details.arrivalAirport})`;
        } else if (travelData.type === 'train' && details.arrivalStation) {
          locationStr = ` (${details.arrivalStation})`;
        } else if (travelData.type === 'bus' && details.arrivalStop) {
          locationStr = ` (${details.arrivalStop})`;
        }

        scheduleItems.push(`Arrival: ${dateStr} ${timeStr}${locationStr}`);

        // Add timezone if available
        if (details.arrivalTimezone) {
          scheduleItems.push(`Arrival timezone: ${details.arrivalTimezone}`);
        }
      } catch (e) {
        console.warn('Error formatting arrival time:', e);
        scheduleItems.push(`Arrival: ${details.arrivalTime}`);
      }
    }

    // Duration (calculate if not provided)
    if (details.duration) {
      scheduleItems.push(`Duration: ${details.duration}`);
    } else if (details.departureTime && details.arrivalTime) {
      try {
        const duration = this.calculateDuration(details.departureTime, details.arrivalTime);
        if (duration) {
          scheduleItems.push(`Duration: ${duration}`);
        }
      } catch (e) {
        console.warn('Error calculating duration:', e);
      }
    }

    // Additional details
    if (details.departureTerminal) {
      scheduleItems.push(`Departure terminal: ${details.departureTerminal}`);
    }
    if (details.arrivalTerminal) {
      scheduleItems.push(`Arrival terminal: ${details.arrivalTerminal}`);
    }
    if (details.gate) {
      scheduleItems.push(`Gate: ${details.gate}`);
    }
    if (details.platform) {
      scheduleItems.push(`Platform: ${details.platform}`);
    }

    if (scheduleItems.length === 0) {
      console.log('✈️ [DEBUG] generateTravelScheduleSection returning null - no schedule items');
      return null;
    }

    console.log('✈️ [DEBUG] generateTravelScheduleSection returning section with items:', scheduleItems);
    return {
      type: 'list',
      title: 'Schedule',
      items: scheduleItems
    };
  }

  /**
   * Generate empty content for missing data
   * @param {Object} pageContext - Page context
   * @returns {Object} Empty content
   */
  generateEmptyContent(pageContext) {
    return {
      sections: [{
        type: 'text',
        title: 'No Details Available',
        content: `No detailed information is available for this ${pageContext.type.toLowerCase().replace(' detail', '')}.`
      }]
    };
  }

  /**
   * Generate notes section
   * @param {string} notes - Notes text
   * @returns {Object} Notes section
   */
  generateNotesSection(notes) {
    return {
      type: 'text',
      title: 'Notes',
      content: notes
    };
  }

  /**
   * Generate details section - MISSING METHOD FIX
   * This method was referenced but missing, causing "this.generateDetailsSection is not a function" errors
   * @param {Object} data - Data object
   * @param {string} title - Section title
   * @returns {Object} Details section
   */
  generateDetailsSection(data, title = 'Details') {
    return this.generateBasicInfoSection(data, title);
  }

  // Additional missing section generators to prevent runtime errors

  /**
   * Generate related accommodation section
   * @param {Object} dayData - Day data with accommodation
   * @returns {Object} Related accommodation section
   */
  generateRelatedAccommodationSection(dayData) {
    const accommodation = dayData.accommodation || dayData.hotel;

    return {
      type: 'table',
      title: 'Accommodation',
      columns: ['Field', 'Value'],
      rows: [
        ['Name', accommodation.name || '-'],
        ['Type', accommodation.type || '-'],
        ['Address', accommodation.address || accommodation.location || '-'],
        ['Check-in', accommodation.checkIn ? this.formatDate(accommodation.checkIn) : '-'],
        ['Check-out', accommodation.checkOut ? this.formatDate(accommodation.checkOut) : '-']
      ]
    };
  }

  /**
   * Generate related travel section
   * @param {Array} travelData - Travel data array
   * @returns {Object} Related travel section
   */
  generateRelatedTravelSection(travelData) {
    return {
      type: 'table',
      title: 'Travel Arrangements',
      columns: ['Type', 'Route', 'Time', 'Details'],
      rows: travelData.map(travel => [
        this.formatTravelType(travel.type),
        this.formatRoute(travel),
        travel.departureTime ? this.formatDateTime(travel.departureTime) : '-',
        travel.flightNumber || travel.trainNumber || travel.busNumber || '-'
      ])
    };
  }

  /**
   * Generate operational info section for places
   * @param {Object} placeData - Place data
   * @returns {Object} Operational info section
   */
  generateOperationalInfoSection(placeData) {
    const rows = [];

    if (placeData.openingHours) rows.push(['Opening Hours', placeData.openingHours]);
    if (placeData.pricing) rows.push(['Pricing', placeData.pricing]);
    if (placeData.cost) rows.push(['Cost', placeData.cost]);

    return {
      type: 'table',
      title: 'Operational Information',
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate reviews section for places
   * @param {Object} placeData - Place data
   * @returns {Object} Reviews section
   */
  generateReviewsSection(placeData) {
    const rows = [];

    if (placeData.rating) rows.push(['Rating', `${placeData.rating}/5`]);
    if (placeData.reviews) {
      if (Array.isArray(placeData.reviews)) {
        placeData.reviews.forEach((review, index) => {
          rows.push([`Review ${index + 1}`, review.text || review]);
        });
      } else {
        rows.push(['Reviews', placeData.reviews]);
      }
    }

    return {
      type: 'table',
      title: 'Reviews & Ratings',
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate stay details section for accommodation
   * @param {Object} accommodationData - Accommodation data
   * @returns {Object} Stay details section
   */
  generateStayDetailsSection(accommodationData) {
    const rows = [];

    if (accommodationData.checkIn) rows.push(['Check-in Date', this.formatDate(accommodationData.checkIn)]);
    if (accommodationData.checkOut) rows.push(['Check-out Date', this.formatDate(accommodationData.checkOut)]);
    if (accommodationData.guests) rows.push(['Guests', accommodationData.guests]);
    if (accommodationData.roomType) rows.push(['Room Type', accommodationData.roomType]);
    if (accommodationData.cost) rows.push(['Cost', accommodationData.cost]);

    return {
      type: 'table',
      title: 'Stay Details',
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate amenities section for accommodation
   * @param {Array} amenities - Amenities array
   * @returns {Object} Amenities section
   */
  generateAmenitiesSection(amenities) {
    return {
      type: 'text',
      title: 'Amenities',
      content: amenities.join(', ')
    };
  }

  /**
   * Generate policies section for accommodation
   * @param {Object} accommodationData - Accommodation data
   * @returns {Object} Policies section
   */
  generatePoliciesSection(accommodationData) {
    const rows = [];

    if (accommodationData.checkInTime) rows.push(['Check-in Time', accommodationData.checkInTime]);
    if (accommodationData.checkOutTime) rows.push(['Check-out Time', accommodationData.checkOutTime]);
    if (accommodationData.policies) rows.push(['Policies', accommodationData.policies]);

    return {
      type: 'table',
      title: 'Policies & Rules',
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate contact and booking section for accommodation
   * @param {Object} accommodationData - Accommodation data
   * @returns {Object} Contact and booking section
   */
  generateContactBookingSection(accommodationData) {
    const rows = [];

    if (accommodationData.phone) rows.push(['Phone', accommodationData.phone]);
    if (accommodationData.email) rows.push(['Email', accommodationData.email]);
    if (accommodationData.website) rows.push(['Website', accommodationData.website]);
    if (accommodationData.confirmationNumber) rows.push(['Confirmation Number', accommodationData.confirmationNumber]);
    if (accommodationData.bookingReference) rows.push(['Booking Reference', accommodationData.bookingReference]);

    return {
      type: 'table',
      title: 'Contact & Booking Information',
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate Booking Information section - BULLET POINT FORMAT
   * @param {Object} travelData - Travel data
   * @returns {Object|null} Booking information section
   */
  generateTravelBookingInfoSection(travelData) {
    console.log('✈️ [DEBUG] generateTravelBookingInfoSection called:', {
      hasDetails: !!travelData.details,
      hasCost: !!travelData.cost,
      detailsKeys: travelData.details ? Object.keys(travelData.details) : []
    });

    const details = travelData.details || {};
    const cost = travelData.cost;
    const bookingItems = [];

    // Cost
    if (cost && cost.amount && cost.amount > 0) {
      const costDisplay = this.formatAccommodationCurrency(cost.amount, cost.currency);
      bookingItems.push(`Cost: ${costDisplay}`);

      // Payment status
      const paymentStatus = cost.paid ? 'Paid' : 'Unpaid';
      bookingItems.push(`Status: ${paymentStatus}`);
    }

    // Ticket numbers (for flights)
    if (Array.isArray(details.ticketNumbers) && details.ticketNumbers.length > 0) {
      const validTickets = details.ticketNumbers.filter(ticket => ticket && ticket.trim());
      if (validTickets.length > 0) {
        bookingItems.push(`Ticket numbers: ${validTickets.join(', ')}`);
      }
    }

    // Booking reference
    if (details.bookingReference || travelData.bookingReference) {
      const bookingRef = details.bookingReference || travelData.bookingReference;
      bookingItems.push(`Booking reference: ${bookingRef}`);
    }

    // Confirmation number
    if (details.confirmationNumber || travelData.confirmationNumber) {
      const confirmationNum = details.confirmationNumber || travelData.confirmationNumber;
      bookingItems.push(`Confirmation number: ${confirmationNum}`);
    }

    // Booking agent
    if (details.bookingAgent) {
      bookingItems.push(`Booked through: ${details.bookingAgent}`);
    }

    // Booking URL
    if (details.bookingUrl) {
      bookingItems.push(`Booking URL: ${details.bookingUrl}`);
    }

    if (bookingItems.length === 0) {
      console.log('✈️ [DEBUG] generateTravelBookingInfoSection returning null - no booking items');
      return null;
    }

    console.log('✈️ [DEBUG] generateTravelBookingInfoSection returning section with items:', bookingItems);
    return {
      type: 'list',
      title: 'Booking Information',
      items: bookingItems
    };
  }

  /**
   * Generate Passenger Information section - BULLET POINT FORMAT
   * @param {Object} travelData - Travel data
   * @returns {Object|null} Passenger information section
   */
  generateTravelPassengerInfoSection(travelData) {
    console.log('✈️ [DEBUG] generateTravelPassengerInfoSection called:', {
      hasDetails: !!travelData.details,
      hasPassengers: !!(travelData.details?.passengers && Array.isArray(travelData.details.passengers)),
      passengersCount: travelData.details?.passengers?.length || 0
    });

    const details = travelData.details || {};

    // Only flights and trains typically have passenger information
    if (travelData.type !== 'flight' && travelData.type !== 'train') {
      console.log('✈️ [DEBUG] generateTravelPassengerInfoSection returning null - not flight or train');
      return null;
    }

    if (!Array.isArray(details.passengers) || details.passengers.length === 0) {
      console.log('✈️ [DEBUG] generateTravelPassengerInfoSection returning null - no passengers');
      return null;
    }

    const passengerItems = [];

    details.passengers.forEach((passenger, index) => {
      if (!passenger || (!passenger.firstName && !passenger.lastName && !passenger.seat)) {
        return; // Skip empty passenger entries
      }

      const passengerParts = [];

      // Passenger number and name
      const name = `${passenger.firstName || ''} ${passenger.lastName || ''}`.trim();
      if (name) {
        passengerParts.push(`Passenger ${index + 1}: ${name}`);
      } else {
        passengerParts.push(`Passenger ${index + 1}`);
      }

      // Seat
      if (passenger.seat) {
        passengerParts.push(`  Seat: ${passenger.seat}`);
      }

      // Meal preference
      if (passenger.mealPreference && passenger.mealPreference !== 'none') {
        const mealDisplay = passenger.mealPreference.charAt(0).toUpperCase() +
                           passenger.mealPreference.slice(1);
        passengerParts.push(`  Meal: ${mealDisplay}`);
      }

      // Add passenger info to items
      passengerItems.push(...passengerParts);
    });

    if (passengerItems.length === 0) {
      console.log('✈️ [DEBUG] generateTravelPassengerInfoSection returning null - no valid passenger data');
      return null;
    }

    console.log('✈️ [DEBUG] generateTravelPassengerInfoSection returning section with items:', passengerItems);
    return {
      type: 'list',
      title: 'Passenger Information',
      items: passengerItems
    };
  }

  /**
   * Generate Related Documents section - BULLET POINT FORMAT
   * @param {Object} travelData - Travel data with contentBlocks
   * @returns {Object|null} Related documents section
   */
  generateTravelRelatedDocumentsSection(travelData) {
    console.log('✈️ [DEBUG] generateTravelRelatedDocumentsSection called:', {
      hasContentBlocks: !!(travelData.contentBlocks && Array.isArray(travelData.contentBlocks)),
      contentBlocksCount: travelData.contentBlocks?.length || 0
    });

    if (!travelData.contentBlocks || !Array.isArray(travelData.contentBlocks)) {
      console.log('✈️ [DEBUG] generateTravelRelatedDocumentsSection returning null - no contentBlocks');
      return null;
    }

    // Extract document references from content blocks
    const documentRefs = travelData.contentBlocks.filter(block =>
      block && block.type === 'document_reference'
    );

    if (documentRefs.length === 0) {
      console.log('✈️ [DEBUG] generateTravelRelatedDocumentsSection returning null - no document references');
      return null;
    }

    console.log('✈️ [DEBUG] generateTravelRelatedDocumentsSection found document references:', documentRefs);

    const documentList = documentRefs.map(doc =>
      doc.documentTitle || doc.displayText || 'Document'
    );

    console.log('✈️ [DEBUG] generateTravelRelatedDocumentsSection returning section with items:', documentList);
    return {
      type: 'list',
      title: 'Related Documents',
      items: documentList
    };
  }

  /**
   * Generate travel notes section - BULLET POINT FORMAT
   * @param {string} notes - Notes content
   * @param {string} updatedAt - Last updated timestamp
   * @returns {Object|null} Notes section
   */
  generateTravelNotesSection(notes, updatedAt) {
    console.log('✈️ [DEBUG] generateTravelNotesSection called:', {
      hasNotes: !!notes,
      notesLength: notes?.length || 0,
      hasUpdatedAt: !!updatedAt
    });

    // Return null if no notes content exists
    if (!notes || notes.trim() === '') {
      console.log('✈️ [DEBUG] generateTravelNotesSection returning null - no notes content');
      return null;
    }

    // Strip HTML tags and convert to readable plain text
    const content = this.stripHtmlToPlainText(notes);

    // Split content into lines for proper PDF formatting
    let lines = [];

    // Split by double newlines (paragraphs) and single newlines
    const paragraphs = content.split(/\n\n+/);
    paragraphs.forEach(paragraph => {
      const trimmed = paragraph.trim();
      if (trimmed) {
        // Split long paragraphs by single newlines too
        const subLines = trimmed.split(/\n/);
        subLines.forEach(line => {
          const trimmedLine = line.trim();
          if (trimmedLine) {
            lines.push(trimmedLine);
          }
        });
        // Add spacing between paragraphs (except for the last one)
        if (paragraphs.indexOf(paragraph) < paragraphs.length - 1) {
          lines.push(''); // Empty line for spacing
        }
      }
    });

    // Add last edited time if available
    if (updatedAt) {
      const editedDate = new Date(updatedAt).toLocaleDateString();
      if (lines.length > 0) {
        lines.push(''); // Empty line for spacing
      }
      lines.push(`Last edited: ${editedDate}`);
    }

    // Return null if no actual content lines after processing
    if (lines.length === 0) {
      console.log('✈️ [DEBUG] generateTravelNotesSection returning null - no content after processing');
      return null;
    }

    console.log('✈️ [DEBUG] generateTravelNotesSection returning section with items:', lines);
    return {
      type: 'list',
      title: 'Notes',
      items: lines
    };
  }

  /**
   * Generate file details section for documents
   * @param {Object} documentData - Document data
   * @returns {Object} File details section
   */
  generateFileDetailsSection(documentData) {
    const rows = [];

    if (documentData.filename) rows.push(['File Name', documentData.filename]);
    if (documentData.fileSize) rows.push(['File Size', documentData.fileSize]);
    if (documentData.fileType) rows.push(['File Type', documentData.fileType]);
    if (documentData.uploadDate) rows.push(['Upload Date', this.formatDate(documentData.uploadDate)]);

    return {
      type: 'table',
      title: 'File Details',
      columns: ['Field', 'Value'],
      rows: rows
    };
  }

  /**
   * Generate associations section for documents
   * @param {Object} documentData - Document data
   * @returns {Object} Associations section
   */
  generateAssociationsSection(documentData) {
    const content = [];

    if (documentData.associatedDays && documentData.associatedDays.length > 0) {
      content.push(`Associated Days: ${documentData.associatedDays.join(', ')}`);
    }

    if (documentData.associatedPlaces && documentData.associatedPlaces.length > 0) {
      content.push(`Associated Places: ${documentData.associatedPlaces.join(', ')}`);
    }

    if (documentData.tags && documentData.tags.length > 0) {
      content.push(`Tags: ${documentData.tags.join(', ')}`);
    }

    return {
      type: 'text',
      title: 'Associations',
      content: content.join('\n')
    };
  }

  /**
   * Generate content preview section for documents
   * @param {Object} documentData - Document data
   * @returns {Object} Content preview section
   */
  generateContentPreviewSection(documentData) {
    const content = documentData.content || documentData.description || 'No content preview available.';

    return {
      type: 'text',
      title: 'Content Preview',
      content: content
    };
  }

  // Place-specific section generators (replicating PlaceDetails.jsx structure)

  /**
   * Generate place title section with context - REMOVED per user requirements
   * The Place Information section has been completely removed as requested.
   * Title formatting is now handled by the main PDF title generation in pageContextDetector.
   * @param {Object} placeData - Place data
   * @returns {null} Always returns null as this section was removed
   */
  generatePlaceTitleSection(placeData) {
    // This section was removed per user requirements
    // The main PDF title now properly shows:
    // Line 1: Place name
    // Line 2: Geographic context
    return null;
  }

  /**
   * Generate external resources section - BULLET LIST FORMAT per user requirements
   * @param {Object} placeData - Place data
   * @returns {Object} External resources section
   */
  generateExternalResourcesSection(placeData) {
    const resourceItems = [];

    // Default resources (same as PlaceDetails.jsx generateMapLink, generateWikiLink, generateWikitravelLink)
    const mapUrl = this.generateMapLink(placeData);
    resourceItems.push(`Google Maps (${mapUrl})`);

    const wikiUrl = `https://en.wikipedia.org/wiki/${encodeURIComponent(placeData.name)}`;
    resourceItems.push(`Wikipedia (${wikiUrl})`);

    const wikitravelUrl = `https://wikitravel.org/en/${encodeURIComponent(placeData.name)}`;
    resourceItems.push(`Wikitravel (${wikitravelUrl})`);

    // Custom resources (replicates custom resources from placeData.resources)
    if (placeData.resources && Array.isArray(placeData.resources)) {
      placeData.resources.forEach(resource => {
        const name = resource.name || 'Custom Resource';
        const url = resource.url || '';
        resourceItems.push(`${name} (${url})`);
      });
    }

    return {
      type: 'list',
      title: 'External Resources',
      items: resourceItems
    };
  }

  /**
   * Generate MULTIPLE Related Days sections - REPLICATES PlaceDetails.jsx structure exactly
   * Creates separate section for each accommodation (matching actual page behavior)
   * @param {Object} placeData - Place data with contentBlocks and _daysData
   * @returns {Array} Array of related days sections (one per accommodation)
   */
  async generatePlaceRelatedDaysSections(placeData, pageContext = {}) {
    // Check if place has contentBlocks (same logic as PlaceDetails.jsx RelatedDaysSection)
    const hasContentBlocks = placeData.contentBlocks && placeData.contentBlocks.length > 0;
    const days = placeData._daysData || []; // Use the days data added by pageContextDetector

    console.log('🏛️ [PDF RelatedDaysSections] Debug:', {
      placeId: placeData.id,
      placeName: placeData.name,
      hasContentBlocks: hasContentBlocks,
      contentBlocksCount: placeData.contentBlocks?.length || 0,
      daysCount: days?.length || 0,
      daysDataAvailable: days && days.length > 0
    });

    if (!days || days.length === 0) {
      console.log('🏛️ [PDF RelatedDaysSections] Returning empty array: no days data');
      return [];
    }

    // Registry-aware (Issue 17): located_in hotels + their stays_on days, for
    // registry-only trips where placeData.contentBlocks above is empty.
    const registryGroups = {};
    if (placeData.id && pageContext.userId && pageContext.tripId) {
      try {
        const { linkPresentationService } = await import('@trip-planner-monorepo/shared');
        const { models: accommodationModels } = await linkPresentationService.getRelatedEntityModels(
          pageContext.userId, pageContext.tripId, 'place', placeData.id, { surface: 'related' }
        );
        const linkedAccommodations = (accommodationModels || []).filter(m => m.entityType === 'accommodation');

        await Promise.all(linkedAccommodations.map(async (acc) => {
          const { models: dayModels } = await linkPresentationService.getRelatedEntityModels(
            pageContext.userId, pageContext.tripId, 'accommodation', acc.entityId, { surface: 'itinerary' }
          );
          const dayIds = (dayModels || []).filter(m => m.entityType === 'day').map(m => m.entityId);
          if (dayIds.length > 0) {
            registryGroups[`accommodation_${acc.entityId}`] = {
              sourceEntityId: acc.entityId,
              sourceEntityType: 'accommodation',
              sourceEntityName: acc.entityName,
              dayIds
            };
          }
        }));
      } catch (error) {
        console.warn('🏛️ [PDF RelatedDaysSections] Could not resolve registry stays for place', error);
      }
    }

    if (!hasContentBlocks && Object.keys(registryGroups).length === 0) {
      return [];
    }

    // Helper function to find day IDs for a date range (same as PlaceDetails.jsx)
    const findDayIdsForDateRange = (dateRange) => {
      if (!dateRange || !dateRange.start || !dateRange.end) return [];

      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);

      return days
        .filter(day => {
          if (!day.date) return false;
          const dayDate = new Date(day.date);
          return dayDate >= startDate && dayDate <= endDate;
        })
        .map(day => day.id);
    };

    // Group content blocks by source entity for display (same as PlaceDetails.jsx)
    const groupedContentBlocks = (placeData.contentBlocks || [])
      .filter(block => block.type !== 'document_reference')
      .reduce((acc, block) => {
        console.log('🏛️ [PDF RelatedDaysSections] Processing content block:', block);

        const key = `${block.sourceEntityType}_${block.sourceEntityId}`;
        if (!acc[key]) {
          acc[key] = {
            sourceEntityId: block.sourceEntityId,
            sourceEntityType: block.sourceEntityType,
            sourceEntityName: block.sourceEntityName,
            dayIds: []
          };
        }
        // Add all dayIds from this block to the group (same logic as PlaceDetails.jsx)
        if (block.relatedDayIds && Array.isArray(block.relatedDayIds) && block.relatedDayIds.length > 0) {
          console.log('🏛️ [PDF RelatedDaysSections] Adding day IDs:', block.relatedDayIds);
          acc[key].dayIds.push(...block.relatedDayIds);
        } else {
          console.log('🏛️ [PDF RelatedDaysSections] No relatedDayIds found or empty, trying to find from dateRange:', block.relatedDayIds);
          // If relatedDayIds is empty but we have a dateRange, try to find the matching days
          if (block.dateRange) {
            const foundDayIds = findDayIdsForDateRange(block.dateRange);
            console.log('🏛️ [PDF RelatedDaysSections] Found day IDs from dateRange:', foundDayIds);
            if (foundDayIds.length > 0) {
              acc[key].dayIds.push(...foundDayIds);
            }
          }
        }
        return acc;
      }, {});

    console.log('🏛️ [PDF RelatedDaysSections] Grouped content blocks:', groupedContentBlocks);

    // Merge registry-resolved groups (Issue 17): registry takes precedence per
    // accommodation id; legacy groups fill in anything the registry read didn't cover.
    Object.entries(registryGroups).forEach(([key, group]) => {
      const existing = groupedContentBlocks[`${group.sourceEntityType}_${group.sourceEntityId}`];
      if (existing) {
        existing.dayIds = Array.from(new Set([...existing.dayIds, ...group.dayIds]));
      } else {
        groupedContentBlocks[key] = group;
      }
    });

    // Convert to PDF format - CREATE SEPARATE SECTION PER ACCOMMODATION
    const relatedDaysSections = [];

    Object.values(groupedContentBlocks).forEach((group) => {
      // Filter days by this group's dayIds and sort by order (same as PlaceDetails.jsx)
      const relatedDays = days
        .filter(day => group.dayIds.includes(day.id))
        .sort((a, b) => a.order - b.order);

      if (relatedDays.length === 0) return;

      // Create a SEPARATE section for this accommodation (matching PlaceDetails.jsx structure)
      const sectionTitle = `Related Days for ${group.sourceEntityName}`;
      const relatedDaysList = [];

      // Format each day exactly as PlaceDetails.jsx does
      relatedDays.forEach((day) => {
        const dayNumber = day.order + 1;
        let dayDisplay = `Day ${dayNumber}`;

        // Try to format the date more precisely like the component
        if (day.date) {
          try {
            const dateObj = new Date(day.date);
            if (!isNaN(dateObj.getTime())) {
              const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = dateObj.getDate();
              const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });
              const year = dateObj.getFullYear();
              dayDisplay = `Day ${dayNumber} - ${dayName} ${dayNum} ${monthName} ${year}`;
            }
          } catch (dateError) {
            // Keep the simple format as fallback
            const formattedDate = day.date ? this.formatDate(day.date) : 'Undated';
            dayDisplay = `Day ${dayNumber} - ${formattedDate}`;
          }
        }

        relatedDaysList.push(dayDisplay);
      });

      // Add this accommodation's section
      relatedDaysSections.push({
        type: 'list',
        title: sectionTitle, // "Related Days for My Hotel"
        items: relatedDaysList // ["Day 3 - Wed 3 Sept 2025", "Day 4 - Thu 4 Sept 2025"]
      });
    });

    return relatedDaysSections;
  }

  /**
   * Generate SINGLE Related Days section (LEGACY - replaced by generatePlaceRelatedDaysSections)
   * @param {Object} placeData - Place data with contentBlocks and _daysData
   * @returns {Object|null} Related days section or null if no related days
   */
  generatePlaceRelatedDaysSection(placeData) {
    // Check if place has contentBlocks (same logic as PlaceDetails.jsx RelatedDaysSection)
    const hasContentBlocks = placeData.contentBlocks && placeData.contentBlocks.length > 0;
    const days = placeData._daysData || []; // Use the days data added by pageContextDetector

    console.log('🏛️ [PDF RelatedDaysSection] Debug:', {
      placeId: placeData.id,
      placeName: placeData.name,
      hasContentBlocks: hasContentBlocks,
      contentBlocksCount: placeData.contentBlocks?.length || 0,
      daysCount: days?.length || 0,
      daysDataAvailable: days && days.length > 0
    });

    if (!hasContentBlocks || !days || days.length === 0) {
      console.log('🏛️ [PDF RelatedDaysSection] Returning null:', {
        hasContentBlocks,
        hasDays: days && days.length > 0
      });
      return null;
    }

    // Helper function to find day IDs for a date range (same as PlaceDetails.jsx)
    const findDayIdsForDateRange = (dateRange) => {
      if (!dateRange || !dateRange.start || !dateRange.end) return [];

      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);

      return days
        .filter(day => {
          if (!day.date) return false;
          const dayDate = new Date(day.date);
          return dayDate >= startDate && dayDate <= endDate;
        })
        .map(day => day.id);
    };

    // Group content blocks by source entity for display (same as PlaceDetails.jsx)
    const groupedContentBlocks = placeData.contentBlocks
      .filter(block => block.type !== 'document_reference')
      .reduce((acc, block) => {
        console.log('🏛️ [PDF RelatedDaysSection] Processing content block:', block);

        const key = `${block.sourceEntityType}_${block.sourceEntityId}`;
        if (!acc[key]) {
          acc[key] = {
            sourceEntityId: block.sourceEntityId,
            sourceEntityType: block.sourceEntityType,
            sourceEntityName: block.sourceEntityName,
            dayIds: []
          };
        }
        // Add all dayIds from this block to the group (same logic as PlaceDetails.jsx)
        if (block.relatedDayIds && Array.isArray(block.relatedDayIds) && block.relatedDayIds.length > 0) {
          console.log('🏛️ [PDF RelatedDaysSection] Adding day IDs:', block.relatedDayIds);
          acc[key].dayIds.push(...block.relatedDayIds);
        } else {
          console.log('🏛️ [PDF RelatedDaysSection] No relatedDayIds found or empty, trying to find from dateRange:', block.relatedDayIds);
          // If relatedDayIds is empty but we have a dateRange, try to find the matching days
          if (block.dateRange) {
            const foundDayIds = findDayIdsForDateRange(block.dateRange);
            console.log('🏛️ [PDF RelatedDaysSection] Found day IDs from dateRange:', foundDayIds);
            if (foundDayIds.length > 0) {
              acc[key].dayIds.push(...foundDayIds);
            }
          }
        }
        return acc;
      }, {});

    console.log('🏛️ [PDF RelatedDaysSection] Grouped content blocks:', groupedContentBlocks);

    // Convert to PDF format (replicating PlaceDetails.jsx structure)
    let relatedDaysList = [];
    let sectionTitle = 'Related Days';

    Object.values(groupedContentBlocks).forEach((group) => {
      // Filter days by this group's dayIds and sort by order (same as PlaceDetails.jsx)
      const relatedDays = days
        .filter(day => group.dayIds.includes(day.id))
        .sort((a, b) => a.order - b.order);

      if (relatedDays.length === 0) return;

      // For the title, use the accommodation name directly (matching PlaceDetails.jsx)
      sectionTitle = `Related Days for ${group.sourceEntityName}`;

      // Format each day exactly as PlaceDetails.jsx does
      relatedDays.forEach((day) => {
        const dayNumber = day.order + 1;
        const formattedDate = day.date ? this.formatDate(day.date) : 'Undated';
        // Use simple format to match "EEE d MMM yyyy" from date-fns format in PlaceDetails.jsx
        let dayDisplay = `Day ${dayNumber} - ${formattedDate}`;

        // Try to format the date more precisely like the component
        if (day.date) {
          try {
            const dateObj = new Date(day.date);
            if (!isNaN(dateObj.getTime())) {
              const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = dateObj.getDate();
              const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });
              const year = dateObj.getFullYear();
              dayDisplay = `Day ${dayNumber} - ${dayName} ${dayNum} ${monthName} ${year}`;
            }
          } catch (dateError) {
            // Keep the simple format as fallback
            dayDisplay = `Day ${dayNumber} - ${formattedDate}`;
          }
        }

        relatedDaysList.push(dayDisplay);
      });
    });

    if (relatedDaysList.length === 0) {
      return null;
    }

    return {
      type: 'list',
      title: sectionTitle, // "Related Days for Another hotel"
      items: relatedDaysList // Direct list items: "Day 1 - Mon 1 Sep 2025"
    };
  }

  /**
   * Generate Related Documents section - REPLICATES RelatedDocumentsSection component from shared package
   * @param {Object} placeData - Place data with contentBlocks
   * @returns {Object|null} Related documents section or null if no related documents
   */
  generatePlaceRelatedDocumentsSection(placeData) {
    if (!placeData.contentBlocks || !Array.isArray(placeData.contentBlocks)) {
      return null;
    }

    // Extract document references from content blocks (same logic as RelatedDocumentsSection)
    const documentRefs = placeData.contentBlocks.filter(block =>
      block && block.type === 'document_reference'
    );

    if (documentRefs.length === 0) {
      return null;
    }

    console.log('🏛️ [PDF RelatedDocuments] Found document references:', documentRefs);

    const documentList = documentRefs.map(doc =>
      doc.documentTitle || doc.displayText || 'Document'
    );

    return {
      type: 'list',
      title: 'Related Documents',
      items: documentList
    };
  }

  /**
   * Generate place notes section - REPLICATES Notes component from PlaceDetails.jsx
   * @param {string} userContent - User content/notes
   * @returns {Object} Notes section
   */
  generatePlaceNotesSection(userContent) {
    let content = userContent || 'No notes for this place.';

    // Strip HTML tags and convert to readable plain text (same as day notes)
    if (content && content !== 'No notes for this place.') {
      content = this.stripHtmlToPlainText(content);
    }

    // Split content into lines for proper PDF formatting
    let lines = [];
    if (content === 'No notes for this place.') {
      lines = [content];
    } else {
      // Split by double newlines (paragraphs) and single newlines
      const paragraphs = content.split(/\n\n+/);
      paragraphs.forEach(paragraph => {
        const trimmed = paragraph.trim();
        if (trimmed) {
          // Split long paragraphs by single newlines too
          const subLines = trimmed.split(/\n/);
          subLines.forEach(line => {
            const trimmedLine = line.trim();
            if (trimmedLine) {
              lines.push(trimmedLine);
            }
          });
          // Add spacing between paragraphs (except for the last one)
          if (paragraphs.indexOf(paragraph) < paragraphs.length - 1) {
            lines.push(''); // Empty line for spacing
          }
        }
      });
    }

    return {
      type: 'list',
      title: 'Notes',
      items: lines
    };
  }

  /**
   * Generate AI-generated resources section (matching External Resources format)
   * @param {Object} placeData - Place data with aiResources property
   * @returns {Object|null} AI resources section or null if no resources
   */
  generateAIResourcesSection(placeData) {
    // Check if AI resources exist
    if (!placeData.aiResources || typeof placeData.aiResources !== 'object' || Object.keys(placeData.aiResources).length === 0) {
      return null;
    }

    // Category name mapping to display format
    const categoryDisplayNames = {
      'general': 'General Information',
      'restaurants': 'Restaurants & Cafes',
      'attractions': 'Attractions',
      'hotels': 'Hotels',
      'shopping': 'Shopping',
      'entertainment': 'Entertainment',
      'transport': 'Transport'
    };

    const resourceItems = [];

    // Iterate through each category (restaurants, attractions, hotels, etc.)
    const categories = Object.keys(placeData.aiResources).sort();

    categories.forEach(category => {
      const resources = placeData.aiResources[category];

      // Skip empty categories
      if (!Array.isArray(resources) || resources.length === 0) {
        return;
      }

      // Add category header with proper display name (marked with HEADING: prefix for PDF formatting)
      const categoryName = categoryDisplayNames[category.toLowerCase()] ||
                          (category.charAt(0).toUpperCase() + category.slice(1));
      resourceItems.push(`HEADING:${categoryName}`);

      // Add each resource - NO bullet prefix (addListSection adds the hyphen)
      resources.forEach(resource => {
        if (resource && resource.name && resource.url) {
          const description = resource.description ? ` - ${resource.description}` : '';
          resourceItems.push(`${resource.name} (${resource.url})${description}`);
        }
      });

      // Add spacing between categories
      resourceItems.push('');
    });

    // Remove trailing empty line
    if (resourceItems.length > 0 && resourceItems[resourceItems.length - 1] === '') {
      resourceItems.pop();
    }

    return {
      type: 'list',
      title: 'AI-Generated Resources',
      items: resourceItems
    };
  }

  /**
   * Generate previously deleted AI resources section (matching External Resources format)
   * @param {Object} placeData - Place data with deletedAIResources property
   * @returns {Object|null} Deleted resources section or null if no deleted resources
   */
  generateDeletedAIResourcesSection(placeData) {
    // Check if deleted AI resources exist
    if (!placeData.deletedAIResources || !Array.isArray(placeData.deletedAIResources) || placeData.deletedAIResources.length === 0) {
      return null;
    }

    const deletedItems = [];

    // Add all deleted resources as a flat list (NO category grouping)
    placeData.deletedAIResources.forEach(resource => {
      if (resource && resource.name && resource.url) {
        // Add each deleted resource - NO bullet prefix (addListSection adds the hyphen)
        // NO deletion date per user request
        // NO category grouping per user request
        deletedItems.push(`${resource.name} (${resource.url})`);
      }
    });

    return {
      type: 'list',
      title: 'Previously Deleted AI Resources',
      items: deletedItems
    };
  }

  /**
   * Generate additional place information (if any exists beyond standard sections)
   * @param {Object} placeData - Place data
   * @returns {Object|null} Additional info section or null
   */
  generatePlaceAdditionalInfo(placeData) {
    const additionalRows = [];

    // Add any additional fields that aren't covered in other sections
    if (placeData.address) additionalRows.push(['Address', placeData.address]);
    if (placeData.city) additionalRows.push(['City', placeData.city]);
    if (placeData.country) additionalRows.push(['Country', placeData.country]);
    if (placeData.latitude && placeData.longitude) {
      additionalRows.push(['Coordinates', `${placeData.latitude}, ${placeData.longitude}`]);
    }
    if (placeData.phone) additionalRows.push(['Phone', placeData.phone]);
    if (placeData.email) additionalRows.push(['Email', placeData.email]);
    if (placeData.website) additionalRows.push(['Website', placeData.website]);

    if (additionalRows.length === 0) {
      return null;
    }

    return {
      type: 'table',
      title: 'Additional Information',
      columns: ['Field', 'Value'],
      rows: additionalRows
    };
  }

  /**
   * Generate map link for place - REPLICATES generateMapLink from PlaceDetails.jsx
   * @param {Object} placeData - Place data
   * @returns {string} Google Maps URL
   */
  generateMapLink(placeData) {
    if (placeData.latitude && placeData.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${placeData.latitude},${placeData.longitude}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeData.name + (placeData.address ? ', ' + placeData.address : ''))}`;
  }

  // Accommodation-specific section generators (replicating AccommodationDetails.jsx structure)

  /**
   * Generate Address section with contact info - BULLET POINT FORMAT - ENHANCED DATA EXTRACTION
   * @param {Object} accommodationData - Accommodation data
   * @returns {Object|null} Address section
   */
  generateAccommodationAddressSection(accommodationData) {
    console.log('🏨 [DEBUG] generateAccommodationAddressSection called:', {
      hasDetails: !!accommodationData.details,
      detailsKeys: accommodationData.details ? Object.keys(accommodationData.details) : [],
      topLevelKeys: Object.keys(accommodationData),
      address: accommodationData.details?.address,
      phone: accommodationData.details?.phone,
      website: accommodationData.details?.website,
      // Check alternative locations for missing data
      topLevelAddress: accommodationData.address,
      topLevelPhone: accommodationData.phone,
      topLevelWebsite: accommodationData.website
    });

    const details = accommodationData.details;
    const addressItems = [];

    // Address - ONLY use actual address fields, NOT place names
    const address = details?.address || accommodationData.address;
    // REMOVED: Do NOT use city/country as address fallback
    // This was incorrectly showing place names like "Seville, Spain" as addresses

    if (address) {
      addressItems.push(address);
      console.log('🏨 [DEBUG] Added address item:', address);
    }

    // Phone number - check multiple possible locations
    const phone = details?.phone || accommodationData.phone;
    if (phone) {
      addressItems.push(`Phone: ${phone}`);
      console.log('🏨 [DEBUG] Added phone item:', `Phone: ${phone}`);
    }

    // Website URL - check multiple possible locations (url is the correct field name)
    const website = details?.url || details?.website || accommodationData.url || accommodationData.website;
    if (website) {
      addressItems.push(`Website: ${website}`);
      console.log('🏨 [DEBUG] Added website item:', `Website: ${website}`);
    }

    if (addressItems.length === 0) {
      console.log('🏨 [DEBUG] generateAccommodationAddressSection returning null - no address items');
      return null;
    }

    console.log('🏨 [DEBUG] generateAccommodationAddressSection returning section with items:', addressItems);
    return {
      type: 'list',
      title: 'Address',
      items: addressItems
    };
  }

  /**
   * Generate Check-in/Check-out section - BULLET POINT FORMAT
   * @param {Object} accommodationData - Accommodation data
   * @returns {Object|null} Stay information section
   */
  generateAccommodationStayInfoSection(accommodationData) {
    console.log('🏨 [CRITICAL TIME DEBUG] generateAccommodationStayInfoSection called:', {
      hasDetails: !!accommodationData.details,
      detailsKeys: accommodationData.details ? Object.keys(accommodationData.details) : [],
      checkInDate: accommodationData.details?.checkInDate,
      checkOutDate: accommodationData.details?.checkOutDate,
      // ADDITIONAL TIME FIELD INVESTIGATION
      checkInTime: accommodationData.details?.checkInTime,
      checkOutTime: accommodationData.details?.checkOutTime,
      allAccommodationKeys: Object.keys(accommodationData),
      completeDetailsObject: accommodationData.details
    });

    const details = accommodationData.details;
    if (!details || (!details.checkInDate && !details.checkOutDate)) {
      console.log('🏨 [DEBUG] generateAccommodationStayInfoSection returning null - no details or dates');
      return null;
    }

    const stayItems = [];

    // Check-in date and time - Format: "Check-in: Sep 9, 2025 2:00 PM" (if time exists)
    if (details.checkInDate) {
      const checkInFormatted = this.formatAccommodationDateTime(
        details.checkInDate,
        details.checkInTime
      );
      stayItems.push(`Check-in: ${checkInFormatted}`);
      console.log('🏨 [CRITICAL TIME DEBUG] Added check-in item:', `Check-in: ${checkInFormatted}`);
    }

    // Check-out date and time - Format: "Check-out: Sep 10, 2025 11:30 AM" (if time exists)
    if (details.checkOutDate) {
      const checkOutFormatted = this.formatAccommodationDateTime(
        details.checkOutDate,
        details.checkOutTime
      );
      stayItems.push(`Check-out: ${checkOutFormatted}`);
      console.log('🏨 [CRITICAL TIME DEBUG] Added check-out item:', `Check-out: ${checkOutFormatted}`);
    }

    // Length of stay - Format: "Length of stay: 1 night"
    if (details.checkInDate && details.checkOutDate) {
      const nights = this.calculateAccommodationNights(details.checkInDate, details.checkOutDate);
      if (nights > 0) {
        stayItems.push(`Length of stay: ${nights} ${nights === 1 ? 'night' : 'nights'}`);
        console.log('🏨 [DEBUG] Added length of stay item:', `Length of stay: ${nights} ${nights === 1 ? 'night' : 'nights'}`);
      }
    }

    if (stayItems.length === 0) {
      console.log('🏨 [DEBUG] generateAccommodationStayInfoSection returning null - no stay items');
      return null;
    }

    return {
      type: 'list',
      title: 'Check-in/Check-out',
      items: stayItems
    };
  }

  /**
   * Generate Related Days section - REPLICATES RelatedDaysSection component
   * @param {Object} accommodationData - Accommodation data with contentBlocks and _daysData
   * @returns {Object|null} Related days section or null if no related days
   */
  async generateAccommodationRelatedDaysSection(accommodationData, pageContext = {}) {
    console.log('🏨 [DEBUG] generateAccommodationRelatedDaysSection called:', {
      accommodationId: accommodationData.id,
      accommodationName: accommodationData.details?.name || accommodationData.summary,
      hasDetails: !!accommodationData.details,
      hasDaysData: !!(accommodationData._daysData && accommodationData._daysData.length > 0)
    });

    // Check if accommodation has contentBlocks (same logic as RelatedDaysSection)
    const hasContentBlocks = accommodationData.contentBlocks && accommodationData.contentBlocks.length > 0;
    const days = accommodationData._daysData || []; // Use the days data added by pageContextDetector

    console.log('🏨 [PDF AccommodationRelatedDays] Debug:', {
      accommodationId: accommodationData.id,
      accommodationName: accommodationData.details?.name || accommodationData.summary,
      hasContentBlocks: hasContentBlocks,
      contentBlocksCount: accommodationData.contentBlocks?.length || 0,
      daysCount: days?.length || 0,
      daysDataAvailable: days && days.length > 0,
      checkInDate: accommodationData.details?.checkInDate,
      checkOutDate: accommodationData.details?.checkOutDate
    });

    if (!days || days.length === 0) {
      console.log('🏨 [PDF AccommodationRelatedDays] Returning null: no days data');
      return null;
    }

    // Extract day IDs from content blocks (same logic as RelatedDaysSection)
    const dayIds = [];

    // Registry-aware (Issue 17): stays_on links, for registry-only trips where
    // contentBlocks above is empty (that's the whole point of registry_only).
    if (accommodationData.id && pageContext.userId && pageContext.tripId) {
      try {
        const { linkPresentationService } = await import('@trip-planner-monorepo/shared');
        const { models } = await linkPresentationService.getRelatedEntityModels(
          pageContext.userId, pageContext.tripId, 'accommodation', accommodationData.id, { surface: 'itinerary' }
        );
        (models || [])
          .filter(m => m.entityType === 'day')
          .forEach(m => {
            if (!dayIds.includes(m.entityId)) {
              dayIds.push(m.entityId);
            }
          });
      } catch (error) {
        console.warn('🏨 [PDF AccommodationRelatedDays] Could not resolve registry stays_on links', error);
      }
    }

    if (hasContentBlocks) {
      console.log('🏨 [PDF AccommodationRelatedDays] Processing all contentBlocks:', {
        totalBlocks: accommodationData.contentBlocks.length,
        blockStructures: accommodationData.contentBlocks.map((block, index) => ({
          index,
          type: block.type,
          sourceEntityType: block.sourceEntityType,
          hasRelatedDayIds: !!(block.relatedDayIds && Array.isArray(block.relatedDayIds)),
          relatedDayIdsLength: block.relatedDayIds?.length || 0,
          relatedDayIds: block.relatedDayIds,
          hasDateRange: !!block.dateRange,
          dateRange: block.dateRange
        }))
      });

      accommodationData.contentBlocks.forEach(block => {
        console.log('🏨 [PDF AccommodationRelatedDays] Processing contentBlock:', {
          blockType: block.type,
          sourceEntityType: block.sourceEntityType,
          hasRelatedDayIds: !!(block.relatedDayIds && Array.isArray(block.relatedDayIds)),
          relatedDayIds: block.relatedDayIds,
          hasDateRange: !!block.dateRange
        });

        if (block.relatedDayIds && Array.isArray(block.relatedDayIds)) {
          block.relatedDayIds.forEach(id => {
            if (!dayIds.includes(id)) dayIds.push(id);
          });
          console.log('🏨 [PDF AccommodationRelatedDays] Added day IDs:', block.relatedDayIds);
        } else if (block.dateRange && block.dateRange.start && block.dateRange.end) {
          // Try to find days from date range if relatedDayIds is missing
          console.log('🏨 [PDF AccommodationRelatedDays] Trying to extract days from dateRange:', block.dateRange);
          const startDate = new Date(block.dateRange.start);
          const endDate = new Date(block.dateRange.end);

          const matchingDays = days.filter(day => {
            if (!day.date) return false;
            const dayDate = new Date(day.date);
            return dayDate >= startDate && dayDate <= endDate;
          });

          matchingDays.forEach(day => {
            if (!dayIds.includes(day.id)) {
              dayIds.push(day.id);
              console.log('🏨 [PDF AccommodationRelatedDays] Added day ID from dateRange:', day.id);
            }
          });
        }
      });
    }

    if (dayIds.length === 0) {
      console.log('🏨 [PDF AccommodationRelatedDays] No day IDs extracted from contentBlocks, trying date-based fallback');

      // Fallback: Use check-in/check-out dates to find related days
      if (accommodationData.details?.checkInDate && days.length > 0) {
        const checkInDate = new Date(accommodationData.details.checkInDate);
        const checkOutDate = accommodationData.details.checkOutDate ? new Date(accommodationData.details.checkOutDate) : checkInDate;

        console.log('🏨 [PDF AccommodationRelatedDays] Using date-based fallback:', {
          checkInDate: checkInDate.toISOString(),
          checkOutDate: checkOutDate.toISOString(),
          availableDays: days.map(day => ({ id: day.id, date: day.date, title: day.title }))
        });

        const dateRelatedDays = days.filter(day => {
          if (!day.date) return false;
          const dayDate = new Date(day.date);
          // Only include the check-in date (start of stay), not check-out date
          // This matches actual accommodation behavior where accommodation is linked to check-in day
          const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
          const checkInDateOnly = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate());
          return dayDateOnly.getTime() === checkInDateOnly.getTime();
        });

        if (dateRelatedDays.length > 0) {
          console.log('🏨 [PDF AccommodationRelatedDays] Found days by date match:', dateRelatedDays.map(d => ({ id: d.id, date: d.date, title: d.title })));

          const items = dateRelatedDays
            .sort((a, b) => a.order - b.order)
            .map(day => {
              const dayNumber = day.order + 1; // Use correct day numbering (order + 1)
              const dayDate = new Date(day.date);
              const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = dayDate.getDate();
              const monthName = dayDate.toLocaleDateString('en-US', { month: 'short' });
              const year = dayDate.getFullYear();
              // Use same format as contentBlocks processing: "Day X - Weekday DD MMM YYYY" (no title suffix)
              return `Day ${dayNumber} - ${dayName} ${dayNum} ${monthName} ${year}`;
            });

          console.log('🏨 [DEBUG] generateAccommodationRelatedDaysSection returning section with date-based items:', items);
          return {
            type: 'list',
            title: 'Related Days',
            items: items
          };
        }
      }

      console.log('🏨 [PDF AccommodationRelatedDays] No day IDs extracted and no date-based match, returning null');
      return null;
    }

    console.log('🏨 [PDF AccommodationRelatedDays] Extracted day IDs:', dayIds);

    // Filter days by dayIds and sort by order (same as RelatedDaysSection)
    const relatedDays = days
      .filter(day => dayIds.includes(day.id))
      .sort((a, b) => a.order - b.order);

    if (relatedDays.length === 0) {
      console.log('🏨 [PDF AccommodationRelatedDays] No matching days found, returning null');
      return null;
    }

    console.log('🏨 [PDF AccommodationRelatedDays] Found matching days:', relatedDays.map(d => ({ id: d.id, order: d.order, date: d.date })));

    // Format each day exactly as RelatedDaysSection does
    const relatedDaysList = [];
    relatedDays.forEach((day) => {
      const dayNumber = day.order + 1;
      let dayDisplay = `Day ${dayNumber}`;

      // Format the date like the component does
      if (day.date) {
        try {
          const dateObj = new Date(day.date);
          if (!isNaN(dateObj.getTime())) {
            const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
            const dayNum = dateObj.getDate();
            const monthName = dateObj.toLocaleDateString('en-US', { month: 'short' });
            const year = dateObj.getFullYear();
            dayDisplay = `Day ${dayNumber} - ${dayName} ${dayNum} ${monthName} ${year}`;
          }
        } catch (dateError) {
          // Keep the simple format as fallback
          const formattedDate = day.date ? this.formatDate(day.date) : 'Undated';
          dayDisplay = `Day ${dayNumber} - ${formattedDate}`;
        }
      }

      relatedDaysList.push(dayDisplay);
    });

    console.log('🏨 [PDF AccommodationRelatedDays] Returning Related Days section with items:', relatedDaysList);
    return {
      type: 'list',
      title: 'Related Days',
      items: relatedDaysList
    };
  }


  /**
   * Generate Booking Details section - BULLET POINT FORMAT - ENHANCED DATA EXTRACTION
   * @param {Object} accommodationData - Accommodation data
   * @returns {Object|null} Booking details section
   */
  generateAccommodationBookingInfoSection(accommodationData) {
    console.log('🏨 [DEBUG] generateAccommodationBookingInfoSection called:', {
      hasDetails: !!accommodationData.details,
      detailsKeys: accommodationData.details ? Object.keys(accommodationData.details) : [],
      topLevelKeys: Object.keys(accommodationData),
      bookingReference: accommodationData.details?.bookingReference,
      bookingAgent: accommodationData.details?.bookingAgent,
      bookingUrl: accommodationData.details?.bookingUrl,
      // Check alternative locations
      topLevelBookingReference: accommodationData.bookingReference,
      topLevelConfirmationNumber: accommodationData.confirmationNumber
    });

    const details = accommodationData.details;
    const bookingItems = [];

    // Booking Reference - check multiple possible locations
    const bookingReference = details?.bookingReference || accommodationData.bookingReference || accommodationData.confirmationNumber;
    if (bookingReference) {
      bookingItems.push(`Booking reference: ${bookingReference}`);
      console.log('🏨 [DEBUG] Added booking reference item:', `Booking reference: ${bookingReference}`);
    }

    // Booking Agent - check multiple possible locations
    const bookingAgent = details?.bookingAgent || accommodationData.bookingAgent;
    if (bookingAgent) {
      bookingItems.push(`Booking agent: ${bookingAgent}`);
      console.log('🏨 [DEBUG] Added booking agent item:', `Booking agent: ${bookingAgent}`);
    }

    // Booking URL - check multiple possible locations
    const bookingUrl = details?.bookingUrl || accommodationData.bookingUrl;
    if (bookingUrl) {
      bookingItems.push(`Booking URL: ${bookingUrl}`);
      console.log('🏨 [DEBUG] Added booking URL item:', `Booking URL: ${bookingUrl}`);
    }

    if (bookingItems.length === 0) {
      console.log('🏨 [DEBUG] generateAccommodationBookingInfoSection returning null - no booking items');
      return null;
    }

    return {
      type: 'list',
      title: 'Booking Details',
      items: bookingItems
    };
  }

  /**
   * Generate Cost & Status section - BULLET POINT FORMAT
   * @param {Object} accommodationData - Accommodation data
   * @returns {Object|null} Cost and status section
   */
  generateAccommodationCostStatusSection(accommodationData) {
    console.log('🏨 [DEBUG] generateAccommodationCostStatusSection called:', {
      hasCost: !!accommodationData.cost,
      costKeys: accommodationData.cost ? Object.keys(accommodationData.cost) : [],
      costAmount: accommodationData.cost?.amount,
      costCurrency: accommodationData.cost?.currency,
      costPaid: accommodationData.cost?.paid
    });

    const cost = accommodationData.cost;

    const costItems = [];

    // Cost Information
    if (cost && cost.amount && cost.amount > 0) {
      const costDisplay = this.formatAccommodationCurrency(cost.amount, cost.currency);
      costItems.push(`Cost: ${costDisplay}`);

      // Payment Status
      const paymentStatus = cost.paid ? 'Paid' : 'Unpaid';
      costItems.push(`Status: ${paymentStatus}`);
    }

    if (costItems.length === 0) {
      console.log('🏨 [DEBUG] generateAccommodationCostStatusSection returning null - no cost items');
      return null;
    }

    console.log('🏨 [DEBUG] generateAccommodationCostStatusSection returning section with items:', costItems);
    return {
      type: 'list',
      title: 'Cost & Status',
      items: costItems
    };
  }

  /**
   * Generate Related Documents section - REPLICATES RelatedDocumentsSection component
   * @param {Object} accommodationData - Accommodation data with contentBlocks
   * @returns {Object|null} Related documents section or null if no related documents
   */
  generateAccommodationRelatedDocumentsSection(accommodationData) {
    console.log('🏨 [DEBUG] generateAccommodationRelatedDocumentsSection called:', {
      hasContentBlocks: !!(accommodationData.contentBlocks && Array.isArray(accommodationData.contentBlocks)),
      contentBlocksCount: accommodationData.contentBlocks?.length || 0,
      contentBlockTypes: accommodationData.contentBlocks?.map(block => block.type) || []
    });

    if (!accommodationData.contentBlocks || !Array.isArray(accommodationData.contentBlocks)) {
      console.log('🏨 [DEBUG] generateAccommodationRelatedDocumentsSection returning null - no contentBlocks');
      return null;
    }

    // Extract document references from content blocks (same logic as RelatedDocumentsSection)
    const documentRefs = accommodationData.contentBlocks.filter(block =>
      block && block.type === 'document_reference'
    );

    if (documentRefs.length === 0) {
      console.log('🏨 [DEBUG] generateAccommodationRelatedDocumentsSection returning null - no document references');
      return null;
    }

    console.log('🏨 [DEBUG] generateAccommodationRelatedDocumentsSection found document references:', documentRefs);

    const documentList = documentRefs.map(doc =>
      doc.documentTitle || doc.displayText || 'Document'
    );

    console.log('🏨 [DEBUG] generateAccommodationRelatedDocumentsSection returning section with items:', documentList);
    return {
      type: 'list',
      title: 'Related Documents',
      items: documentList
    };
  }

  /**
   * Generate accommodation notes section - REPLICATES Notes component from AccommodationDetails.jsx
   * @param {string} notes - Notes content
   * @param {string} updatedAt - Last updated timestamp
   * @returns {Object} Notes section
   */
  generateAccommodationNotesSection(notes, updatedAt) {
    console.log('🏨 [DEBUG] generateAccommodationNotesSection called:', {
      hasNotes: !!notes,
      notesLength: notes?.length || 0,
      hasUpdatedAt: !!updatedAt,
      updatedAt: updatedAt
    });

    // Return null if no notes content exists (matches other sections pattern)
    if (!notes || notes.trim() === '') {
      console.log('🏨 [DEBUG] generateAccommodationNotesSection returning null: no notes content');
      return null;
    }

    // Strip HTML tags and convert to readable plain text (same as day notes)
    const content = this.stripHtmlToPlainText(notes);

    // Split content into lines for proper PDF formatting
    let lines = [];

    // Split by double newlines (paragraphs) and single newlines
    const paragraphs = content.split(/\n\n+/);
    paragraphs.forEach(paragraph => {
      const trimmed = paragraph.trim();
      if (trimmed) {
        // Split long paragraphs by single newlines too
        const subLines = trimmed.split(/\n/);
        subLines.forEach(line => {
          const trimmedLine = line.trim();
          if (trimmedLine) {
            lines.push(trimmedLine);
          }
        });
        // Add spacing between paragraphs (except for the last one)
        if (paragraphs.indexOf(paragraph) < paragraphs.length - 1) {
          lines.push(''); // Empty line for spacing
        }
      }
    });

    // Add last edited time if available
    if (updatedAt) {
      const editedDate = new Date(updatedAt).toLocaleDateString();
      if (lines.length > 0) {
        lines.push(''); // Empty line for spacing
      }
      lines.push(`Last edited: ${editedDate}`);
    }

    // Return null if no actual content lines after processing
    if (lines.length === 0) {
      console.log('🏨 [DEBUG] generateAccommodationNotesSection returning null: no content after processing');
      return null;
    }

    console.log('🏨 [DEBUG] generateAccommodationNotesSection returning section with items:', lines);
    return {
      type: 'list',
      title: 'Notes',
      items: lines
    };
  }

  /**
   * Combine date and time strings into a single Date object
   * @param {string} dateStr - Date string (e.g., "2025-09-09")
   * @param {string} timeStr - Time string (various formats supported)
   * @returns {Date|null} Combined Date object or null if combination fails
   */
  combineDateAndTime(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;

    try {
      console.log('🏨 [CRITICAL TIME DEBUG] combineDateAndTime called:', {
        dateStr: dateStr,
        timeStr: timeStr,
        timeStrType: typeof timeStr
      });

      // Parse the base date
      const [year, month, day] = dateStr.split('-').map(Number);
      let baseDate = new Date(year, month - 1, day, 0, 0, 0, 0);

      // Handle different time string formats
      if (typeof timeStr === 'string') {
        // Time format like "14:00" or "2:00 PM"
        if (timeStr.includes(':')) {
          const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i);
          if (timeMatch) {
            let hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            const ampm = timeMatch[3];

            // Handle 12-hour format
            if (ampm) {
              if (ampm.toUpperCase() === 'PM' && hours !== 12) {
                hours += 12;
              } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
                hours = 0;
              }
            }

            baseDate.setHours(hours, minutes, 0, 0);
            console.log('🏨 [CRITICAL TIME DEBUG] Successfully parsed time string:', {
              originalTime: timeStr,
              parsedHours: hours,
              parsedMinutes: minutes,
              resultDate: baseDate.toISOString()
            });
            return baseDate;
          }
        }
      } else if (timeStr instanceof Date) {
        // If timeStr is already a Date object, extract time components
        baseDate.setHours(
          timeStr.getHours(),
          timeStr.getMinutes(),
          timeStr.getSeconds(),
          timeStr.getMilliseconds()
        );
        return baseDate;
      }

      console.log('🏨 [CRITICAL TIME DEBUG] Could not parse time string format:', timeStr);
      return null;
    } catch (error) {
      console.error('🏨 [CRITICAL TIME DEBUG] Error in combineDateAndTime:', error);
      return null;
    }
  }

  /**
   * Format date and time for accommodation display - FIXED for timezone and midnight handling
   * @param {string} dateStr - ISO date string
   * @param {string} timeStr - Optional separate time string
   * @returns {string} Formatted date and time
   */
  formatAccommodationDateTime(dateStr, timeStr = null) {
    console.log('🏨 [CRITICAL TIME DEBUG] formatAccommodationDateTime called:', {
      dateStr: dateStr,
      timeStr: timeStr,
      dateStrType: typeof dateStr,
      timeStrType: typeof timeStr,
      isDateOnly: /^\d{4}-\d{2}-\d{2}$/.test(dateStr || '')
    });

    if (!dateStr) return 'Not specified';

    try {
      // Handle both date-only strings and full ISO datetime strings
      let date;

      // If the string is just a date (YYYY-MM-DD), treat it as local midnight UNLESS we have separate time
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        console.log('🏨 [CRITICAL TIME DEBUG] Date-only string detected, checking for separate time field');

        // For date-only strings, create date at local midnight to avoid timezone issues
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day, 0, 0, 0, 0); // Start with local midnight

        // If we have a separate time string, try to apply it
        if (timeStr) {
          console.log('🏨 [CRITICAL TIME DEBUG] Applying separate time field:', timeStr);
          try {
            // Parse the time string and apply to the date
            const combinedDateTime = this.combineDateAndTime(dateStr, timeStr);
            if (combinedDateTime) {
              date = combinedDateTime;
              console.log('🏨 [CRITICAL TIME DEBUG] Successfully combined date and time:', {
                originalDate: dateStr,
                originalTime: timeStr,
                combinedResult: date.toISOString(),
                hours: date.getHours(),
                minutes: date.getMinutes()
              });
            }
          } catch (error) {
            console.error('🏨 [CRITICAL TIME DEBUG] Error combining date and time:', error);
          }
        }
      } else {
        console.log('🏨 [CRITICAL TIME DEBUG] Full datetime string detected');
        // For full datetime strings, parse normally
        date = new Date(dateStr);

        console.log('🏨 [CRITICAL TIME DEBUG] Parsed datetime:', {
          originalHours: date.getHours(),
          originalMinutes: date.getMinutes(),
          fullDateTime: date.toISOString()
        });

        // DO NOT force to midnight if the datetime has actual time information
        // Only force to midnight if it's exactly midnight (indicating date-only storage)
        if (date.getHours() === 0 && date.getMinutes() === 0) {
          console.log('🏨 [CRITICAL TIME DEBUG] Datetime is at midnight - may be date-only data stored as datetime');
        } else {
          console.log('🏨 [CRITICAL TIME DEBUG] Datetime has actual time information - preserving it');
        }
      }

      if (isNaN(date.getTime())) {
        return dateStr;
      }

      console.log('🏨 [PDF DateTime Debug] Formatting accommodation date/time:', {
        original: dateStr,
        parsedDate: date,
        hours: date.getHours(),
        minutes: date.getMinutes(),
        isDateOnly: /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
      });

      // Use the same format pattern as HotelDetails.js formatDateTime function
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();

      // Format time - accommodation dates should always be 12:00 AM (midnight)
      let hour = date.getHours();
      const minute = date.getMinutes();
      const ampm = hour >= 12 ? 'PM' : 'AM';

      // Convert to 12-hour format (handle midnight as 12:00 AM)
      if (hour === 0) {
        hour = 12; // midnight is 12:00 AM
      } else if (hour > 12) {
        hour = hour - 12; // afternoon hours
      }

      const formattedTime = `${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;
      const formattedDate = `${month} ${day}, ${year}`;
      const result = `${formattedDate} ${formattedTime}`;

      console.log('🏨 [PDF DateTime Debug] Formatting result:', {
        formattedDate,
        formattedTime,
        fullResult: result,
        shouldBeMidnight: true
      });

      return result;
    } catch (e) {
      console.error('Error formatting accommodation date/time:', e);
      return dateStr;
    }
  }

  /**
   * Calculate nights duration for accommodation - REPLICATES calculateNights from HotelDetails.js
   * @param {string} checkInDate - Check-in date string
   * @param {string} checkOutDate - Check-out date string
   * @returns {number} Number of nights
   */
  calculateAccommodationNights(checkInDate, checkOutDate) {
    if (!checkInDate || !checkOutDate) return 0;

    try {
      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkOutDate);
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (e) {
      console.error('Error calculating accommodation nights:', e);
      return 0;
    }
  }

  /**
   * Format currency for accommodation display - REPLICATES formatCurrency from HotelDetails.js
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code
   * @returns {string} Formatted currency string
   */
  formatAccommodationCurrency(amount, currency = 'USD') {
    if (amount === undefined || amount === null) return 'Not specified';

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return `${amount} ${currency}`;
    }
  }

  // Utility methods for formatting

  formatFieldName(fieldName) {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  formatValue(value) {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return value?.toString() || '-';
  }

  formatDate(dateString) {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  }

  formatDateTime(dateTimeString) {
    try {
      const date = new Date(dateTimeString);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch (error) {
      return dateTimeString;
    }
  }

  formatTravelType(type) {
    if (!type) return 'Unknown';
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }

  formatRoute(travelData) {
    const origin = travelData.origin || travelData.from || 'Unknown';
    const destination = travelData.destination || travelData.to || 'Unknown';
    return `${origin} → ${destination}`;
  }

  calculateDuration(startTime, endTime) {
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end - start;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHours}h ${diffMinutes}m`;
    } catch (error) {
      return 'Unknown';
    }
  }

  /**
   * Strip HTML tags and convert to readable plain text for PDF export
   * @param {string} htmlContent - HTML content to convert
   * @returns {string} Plain text content
   */
  stripHtmlToPlainText(htmlContent) {
    if (!htmlContent || typeof htmlContent !== 'string') {
      return '';
    }

    let text = htmlContent;

    // Convert common HTML elements to readable text
    text = text.replace(/<h[1-6][^>]*>/gi, '\n\n'); // Headers: add spacing
    text = text.replace(/<\/h[1-6]>/gi, '\n'); // Header close: new line
    text = text.replace(/<p[^>]*>/gi, '\n'); // Paragraphs: new line
    text = text.replace(/<\/p>/gi, '\n'); // Paragraph close: new line
    text = text.replace(/<br\s*\/?>/gi, '\n'); // Line breaks: new line
    text = text.replace(/<li[^>]*>/gi, '\n- '); // List items: simple dashes
    text = text.replace(/<\/li>/gi, ''); // List item close
    text = text.replace(/<ul[^>]*>|<\/ul>/gi, '\n'); // Unordered lists: spacing
    text = text.replace(/<ol[^>]*>|<\/ol>/gi, '\n'); // Ordered lists: spacing
    text = text.replace(/<strong[^>]*>|<\/strong>/gi, ''); // Bold: remove tags
    text = text.replace(/<b[^>]*>|<\/b>/gi, ''); // Bold: remove tags
    text = text.replace(/<em[^>]*>|<\/em>/gi, ''); // Italic: remove tags
    text = text.replace(/<i[^>]*>|<\/i>/gi, ''); // Italic: remove tags

    // Handle links - extract URL and show as "[text] (url)"
    text = text.replace(/<a[^>]*href=["']([^"']*)["'][^>]*>([^<]*)<\/a>/gi, '$2 ($1)');

    // Remove any remaining HTML tags
    text = text.replace(/<[^>]*>/g, '');

    // Decode HTML entities
    text = text.replace(/&amp;/g, '&');
    text = text.replace(/&lt;/g, '<');
    text = text.replace(/&gt;/g, '>');
    text = text.replace(/&quot;/g, '"');
    text = text.replace(/&#39;/g, "'");
    text = text.replace(/&nbsp;/g, ' ');

    // Clean up whitespace
    text = text.replace(/\n\s*\n\s*\n/g, '\n\n'); // Multiple newlines to double
    text = text.replace(/^\s+|\s+$/g, ''); // Trim start/end
    text = text.replace(/[ \t]+/g, ' '); // Multiple spaces to single

    return text;
  }

  /**
   * Get preview information for this layout
   * @param {*} data - Data to analyze
   * @returns {Object} Preview information
   */
  getPreview(data) {
    return {
      estimatedPages: 1,
      sections: ['Basic Information', 'Details', 'Additional Information'],
      features: [
        'Complete detail view',
        'Structured information display',
        'Context-appropriate formatting',
        'Professional layout'
      ],
      dataCount: 1
    };
  }
}